import styled, { css } from 'styled-components';
import { CrudStyles } from '../shared/Crud';

const { Button: CrudButton } = CrudStyles;

export const MOBILE_BREAKPOINT = '768px';

/**
 * Paleta alinhada ao tema padrão do sistema (azul #3B5998).
 * Suporte a prefers-color-scheme: dark para uso noturno.
 */
export const DriverShell = styled.div`
    --driver-page-bg: #f6f5fc;
    --driver-surface: #f8fafc;
    --driver-surface-elevated: #ffffff;
    --driver-text: #222222;
    --driver-text-muted: #6b7280;
    --driver-border: #e6e6e6;
    --driver-border-strong: #bcbcbc;
    --driver-primary: #3b5998;
    --driver-primary-hover: #2e4374;
    --driver-primary-text: #ffffff;
    --driver-accent-soft: #eff6ff;
    --driver-address-bg: #eff6ff;
    --driver-address-border: #5e81ac;
    --driver-shadow: rgba(34, 34, 34, 0.08);
    --driver-nav-bg: #ffffff;
    --driver-input-bg: #ffffff;
    --driver-summary-bg: #f6f5fc;
    --driver-success: #16a34a;
    --driver-success-bg: #f0fdf4;
    --driver-danger: #fc5050;
    --driver-danger-bg: #fee2e2;

    min-height: 100%;
    background: var(--driver-page-bg);
    color: var(--driver-text);

    @media (prefers-color-scheme: dark) {
        --driver-page-bg: #0f1419;
        --driver-surface: #1a2332;
        --driver-surface-elevated: #243044;
        --driver-text: #f1f5f9;
        --driver-text-muted: #94a3b8;
        --driver-border: #334155;
        --driver-border-strong: #475569;
        --driver-primary: #5e81ac;
        --driver-primary-hover: #3b5998;
        --driver-primary-text: #ffffff;
        --driver-accent-soft: #1e293b;
        --driver-address-bg: #1e3a5f;
        --driver-address-border: #3b5998;
        --driver-shadow: rgba(0, 0, 0, 0.35);
        --driver-nav-bg: #243044;
        --driver-input-bg: #1a2332;
        --driver-summary-bg: #1e293b;
        --driver-success: #4ade80;
        --driver-success-bg: #14532d;
        --driver-danger: #f87171;
        --driver-danger-bg: #3f1d1d;
    }
`;

export const PageWrap = styled.div`
    padding: 12px 12px 24px;
    min-width: 0;
    max-width: 480px;
    margin: 0 auto;

    @media (min-width: ${MOBILE_BREAKPOINT}) {
        padding: 16px 16px 32px;
        max-width: 520px;
    }
`;

export const PageTitle = styled.h1`
    margin: 0 0 28px;
    padding-bottom: 14px;
    border-bottom: 2px solid var(--driver-primary);
    font-size: 24px;
    font-weight: 800;
    color: var(--driver-text);
    letter-spacing: -0.02em;
    line-height: 1.2;
`;

export const RoteiroCards = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

export const RoteiroCard = styled.button`
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
    width: 100%;
    padding: 16px;
    border: 1px solid var(--driver-border);
    border-radius: 12px;
    background: var(--driver-surface-elevated);
    box-shadow: 0 2px 6px var(--driver-shadow);
    text-align: left;
    cursor: pointer;
    min-height: 72px;
    transition: border-color 0.15s, box-shadow 0.15s, transform 0.1s;
    color: var(--driver-text);

    &:hover {
        border-color: var(--driver-primary);
        box-shadow: 0 4px 10px var(--driver-shadow);
    }

    &:active {
        transform: scale(0.99);
    }
`;

export const RoteiroCardTitle = styled.span`
    font-size: 18px;
    font-weight: 700;
    line-height: 1.35;
    color: var(--driver-text);
    letter-spacing: -0.01em;
`;

export const RoteiroCardMeta = styled.span`
    font-size: 14px;
    color: var(--driver-text-muted);
`;

export const PontoPanel = styled.div`
    background: var(--driver-surface-elevated);
    border-radius: 12px;
    border: 1px solid var(--driver-border);
    box-shadow: 0 2px 6px var(--driver-shadow);
    padding: 20px 16px;
    display: flex;
    flex-direction: column;
    gap: 0;
`;

export const PontoHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    margin-bottom: 12px;
    padding-bottom: 18px;
    border-bottom: 1px solid var(--driver-border);
`;

export const PontoSwipeHint = styled.p`
    margin: 0 0 16px;
    font-size: 12px;
    font-weight: 500;
    color: var(--driver-text-muted);
    text-align: center;
    letter-spacing: 0.02em;
`;

const slideEase = 'cubic-bezier(0.4, 0, 0.2, 1)';
const slideDuration = '0.25s';

export const PontoSwipeArea = styled.div`
    touch-action: pan-y;
    cursor: grab;
    user-select: none;
    -webkit-user-select: none;
    overflow: hidden;

    &:active {
        cursor: grabbing;
    }
`;

export const PontoSwipeContent = styled.div`
    will-change: transform, opacity;
    transform: translateX(${({ $offset }) => $offset}px);
    opacity: 1;
    transition: ${({ $dragging, $exitDirection, $enterDirection }) => {
        if ($dragging) return 'none';
        if ($exitDirection || $enterDirection) {
            return `transform ${slideDuration} ${slideEase}, opacity ${slideDuration} ${slideEase}`;
        }
        return `transform 0.12s ${slideEase}, opacity 0.12s ${slideEase}`;
    }};

    ${({ $exitDirection }) =>
        $exitDirection === 'next' &&
        `
        transform: translateX(-32%);
        opacity: 0;
    `}

    ${({ $exitDirection }) =>
        $exitDirection === 'prev' &&
        `
        transform: translateX(32%);
        opacity: 0;
    `}

    ${({ $enterDirection }) =>
        $enterDirection === 'next' &&
        `
        animation: pontoEnterFromRight ${slideDuration} ${slideEase} forwards;
    `}

    ${({ $enterDirection }) =>
        $enterDirection === 'prev' &&
        `
        animation: pontoEnterFromLeft ${slideDuration} ${slideEase} forwards;
    `}

    @keyframes pontoEnterFromRight {
        from {
            transform: translateX(32%);
            opacity: 0;
        }
        to {
            transform: translateX(0);
            opacity: 1;
        }
    }

    @keyframes pontoEnterFromLeft {
        from {
            transform: translateX(-32%);
            opacity: 0;
        }
        to {
            transform: translateX(0);
            opacity: 1;
        }
    }
`;

export const PontoHeaderCenter = styled.div`
    flex: 1;
    text-align: center;
    min-width: 0;
    will-change: transform, opacity;

    ${({ $exitDirection }) =>
        $exitDirection === 'next' &&
        `
        transform: translateX(-18%);
        opacity: 0;
        transition: transform ${slideDuration} ${slideEase}, opacity ${slideDuration} ${slideEase};
    `}

    ${({ $exitDirection }) =>
        $exitDirection === 'prev' &&
        `
        transform: translateX(18%);
        opacity: 0;
        transition: transform ${slideDuration} ${slideEase}, opacity ${slideDuration} ${slideEase};
    `}

    ${({ $enterDirection }) =>
        $enterDirection === 'next' &&
        `
        animation: pontoHeaderEnterFromRight ${slideDuration} ${slideEase} forwards;
    `}

    ${({ $enterDirection }) =>
        $enterDirection === 'prev' &&
        `
        animation: pontoHeaderEnterFromLeft ${slideDuration} ${slideEase} forwards;
    `}

    @keyframes pontoHeaderEnterFromRight {
        from {
            transform: translateX(18%);
            opacity: 0;
        }
        to {
            transform: translateX(0);
            opacity: 1;
        }
    }

    @keyframes pontoHeaderEnterFromLeft {
        from {
            transform: translateX(-18%);
            opacity: 0;
        }
        to {
            transform: translateX(0);
            opacity: 1;
        }
    }
`;

export const PontoTitle = styled.h2`
    margin: 0 0 6px;
    font-size: 22px;
    font-weight: 800;
    color: var(--driver-text);
    letter-spacing: -0.02em;
    line-height: 1.2;
`;

export const NavArrowButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 48px;
    height: 48px;
    border: 1px solid var(--driver-border-strong);
    border-radius: 10px;
    background: var(--driver-nav-bg);
    color: var(--driver-primary);
    cursor: pointer;
    flex-shrink: 0;

    &:disabled {
        opacity: 0.35;
        cursor: not-allowed;
    }
`;

export const FieldBlock = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 18px 0 0;

    ${PontoSwipeContent} > &:first-child {
        padding-top: 0;
    }

    & + & {
        margin-top: 4px;
        border-top: 1px solid var(--driver-border);
    }
`;

export const FieldLabel = styled.span`
    display: block;
    font-size: 13px;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.07em;
    color: var(--driver-text);
    padding-left: 12px;
    border-left: 3px solid var(--driver-primary);
    line-height: 1.35;
`;

export const FieldValue = styled.p`
    margin: 0;
    padding-left: 15px;
    font-size: 16px;
    line-height: 1.6;
    color: var(--driver-text);
    white-space: pre-wrap;
`;

export const AddressCard = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-left: 15px;
    padding: 14px;
    background: var(--driver-address-bg);
    border: 1px solid var(--driver-address-border);
    border-radius: 10px;
`;

export const AddressText = styled.p`
    margin: 0;
    font-size: 16px;
    font-weight: 500;
    line-height: 1.5;
    color: var(--driver-text);
`;

const addressActionButtonStyles = `
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    width: 100%;
    min-height: 48px;
    padding: 10px 14px;
    border-radius: 10px;
    font-size: 15px;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.15s, color 0.15s, border-color 0.15s;

    &:active {
        transform: scale(0.98);
    }
`;

export const CopyAddressBtn = styled.button`
    ${addressActionButtonStyles}
    border: 1px solid ${({ $copied }) => ($copied ? 'var(--driver-success)' : 'var(--driver-primary)')};
    background: ${({ $copied }) => ($copied ? 'var(--driver-success-bg)' : 'var(--driver-surface-elevated)')};
    color: ${({ $copied }) => ($copied ? 'var(--driver-success)' : 'var(--driver-primary)')};
`;

export const NavigateAddressBtn = styled.button`
    ${addressActionButtonStyles}
    border: 1px solid var(--driver-border-strong);
    background: var(--driver-surface-elevated);
    color: var(--driver-text);
`;

export const AddressActionMenuWrap = styled.div`
    position: relative;
    width: 100%;
`;

export const AddressActionMenuOverlay = styled.div`
    position: fixed;
    inset: 0;
    z-index: 999;
    background: transparent;
`;

export const AddressActionMenu = styled.div`
    position: absolute;
    top: calc(100% + 8px);
    left: 0;
    right: 0;
    z-index: 1000;
    overflow: hidden;
    border-radius: 10px;
    border: 1px solid var(--driver-border);
    background: var(--driver-surface-elevated);
    box-shadow: 0 8px 24px var(--driver-shadow);
`;

export const AddressActionMenuItem = styled.button`
    display: flex;
    align-items: center;
    width: 100%;
    min-height: 48px;
    padding: 12px 14px;
    border: none;
    background: var(--driver-surface-elevated);
    color: var(--driver-text);
    font-size: 15px;
    font-weight: 500;
    text-align: left;
    cursor: pointer;

    &:not(:last-child) {
        border-bottom: 1px solid var(--driver-border);
    }

    &:hover,
    &:focus-visible {
        background: var(--driver-summary-bg);
        outline: none;
    }
`;

export const ItemsTable = styled.table`
    width: calc(100% - 15px);
    margin-left: 15px;
    border-collapse: collapse;
    font-size: 15px;

    th {
        text-align: left;
        padding: 12px 14px;
        background: var(--driver-summary-bg);
        font-size: 13px;
        font-weight: 800;
        text-transform: uppercase;
        letter-spacing: 0.06em;
        color: var(--driver-text);
        border-bottom: 1px solid var(--driver-border);
    }

    td {
        padding: 12px;
        border-bottom: 1px solid var(--driver-border);
        color: var(--driver-text);
        vertical-align: top;
    }

    tr:last-child td {
        border-bottom: none;
    }

    td:first-child {
        width: 56px;
        font-weight: 700;
    }
`;

const primaryButtonStyles = css`
    width: 100%;
    justify-content: center;
    min-height: 52px;
    font-size: 16px;
    font-weight: 600;
    margin-top: 8px;
    background-color: var(--driver-primary) !important;
    color: var(--driver-primary-text) !important;
    border: none !important;

    &:hover:not(:disabled) {
        background-color: var(--driver-primary-hover) !important;
    }

    &:disabled {
        opacity: 0.45;
        cursor: not-allowed;
    }
`;

export const PrimaryActionButton = styled(CrudButton).attrs({ type: 'button', variant: 'primary' })`
    ${primaryButtonStyles}
`;

export const BackLink = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 20px;
    padding: 8px 0;
    border: none;
    background: none;
    font-size: 15px;
    font-weight: 500;
    color: var(--driver-primary);
    cursor: pointer;
    min-height: 44px;
`;

export const StatusPanel = styled.div`
    background: var(--driver-surface-elevated);
    border-radius: 12px;
    border: 1px solid var(--driver-border);
    box-shadow: 0 2px 8px var(--driver-shadow);
    padding: 16px;
    display: flex;
    flex-direction: column;
    gap: 12px;
    min-height: 320px;
`;

export const StatusPanelHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    margin-bottom: 8px;
    padding-bottom: 16px;
    border-bottom: 1px solid var(--driver-border);
    color: var(--driver-text);
`;

export const SectionTitle = styled.h2`
    margin: 0;
    font-size: 20px;
    font-weight: 800;
    color: var(--driver-text);
    letter-spacing: -0.02em;
    line-height: 1.2;
`;

export const CloseButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;
    border: none;
    border-radius: 8px;
    background: var(--driver-summary-bg);
    color: var(--driver-text-muted);
    cursor: pointer;
`;

export const StatusActionButton = styled(CrudButton).attrs({ type: 'button', variant: 'outline' })`
    width: 100%;
    justify-content: center;
    min-height: 52px;
    font-size: 16px;
    font-weight: 500;
    background: var(--driver-surface-elevated) !important;
    color: var(--driver-text) !important;
    border: 1px solid var(--driver-border-strong) !important;
`;

export const StatusPhotoActions = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
`;

export const StatusPhotoActionButton = styled(StatusActionButton)`
    flex: 1 1 calc(50% - 4px);
    width: auto;
    min-width: calc(50% - 4px);
    font-size: 15px;

    &:only-child {
        flex: 1 1 100%;
        min-width: 100%;
    }

    &:nth-child(3):last-child {
        flex: 1 1 100%;
        min-width: 100%;
    }
`;

export const SignatureModalOverlay = styled.div`
    position: fixed;
    inset: 0;
    box-sizing: border-box;
    padding: 16px;
    overflow: hidden;
    overscroll-behavior: none;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 10000;
`;

export const SignatureModalBox = styled.div`
    box-sizing: border-box;
    background: var(--driver-surface-elevated);
    padding: 20px;
    border-radius: 12px;
    width: 100%;
    max-width: min(480px, calc(100vw - 32px));
    max-height: min(90vh, 90dvh);
    overflow-y: auto;
    box-shadow: 0 4px 20px var(--driver-shadow);
    border: 1px solid var(--driver-border);
`;

export const SignatureModalTitle = styled.h2`
    margin: 0 0 8px;
    font-size: 18px;
    font-weight: 800;
    color: var(--driver-text);
`;

export const SignatureModalHint = styled.p`
    margin: 0 0 12px;
    font-size: 14px;
    line-height: 1.45;
    color: var(--driver-text-muted);
`;

export const SignatureModalCanvasWrap = styled.div`
    width: 100%;
    margin-bottom: 16px;
    border-radius: 10px;
    border: 1px solid var(--driver-border-strong);
    background: #ffffff;
    overflow: hidden;
    touch-action: none;
    -webkit-user-select: none;
    user-select: none;
`;

export const SignatureModalCanvas = styled.canvas`
    display: block;
    width: 100%;
    touch-action: none;
    cursor: crosshair;
    -webkit-tap-highlight-color: transparent;
`;

export const SignatureModalActions = styled.div`
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-end;
    gap: 8px;
    touch-action: manipulation;
`;

export const StatusSummary = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding: 12px;
    background: var(--driver-summary-bg);
    border-radius: 8px;
    font-size: 14px;
    color: var(--driver-text-muted);
`;

export const CommentTextarea = styled.textarea`
    width: 100%;
    min-height: 96px;
    padding: 12px 14px;
    border: 1px solid var(--driver-border-strong);
    border-radius: 8px;
    font-size: 16px;
    font-family: inherit;
    resize: vertical;
    box-sizing: border-box;
    background: var(--driver-input-bg);
    color: var(--driver-text);

    &::placeholder {
        color: var(--driver-text-muted);
    }
`;

export const StatusSelect = styled.select`
    width: 100%;
    min-height: 52px;
    padding: 12px 14px;
    border: 1px solid var(--driver-border-strong);
    border-radius: 8px;
    font-size: 16px;
    background: var(--driver-input-bg);
    color: var(--driver-text);
`;

export const EmptyMessage = styled.p`
    margin: 0;
    font-size: 15px;
    color: var(--driver-text-muted);
    text-align: center;
    padding: 24px 0;
`;

export const UpdatedNotice = styled.p`
    margin: 0;
    padding: 12px 14px;
    border-radius: 10px;
    background: var(--driver-success-bg);
    color: var(--driver-success);
    font-size: 14px;
    font-weight: 600;
    text-align: center;
`;

export const PhotoThumbnailGrid = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
`;

export const PhotoThumbnailItem = styled.div`
    position: relative;
    width: 72px;
    height: 72px;
    flex-shrink: 0;
    border-radius: 10px;
    overflow: hidden;
    border: 1px solid var(--driver-border);
    background: var(--driver-summary-bg);
`;

export const PhotoThumbnailImage = styled.img`
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
`;

export const PhotoRemoveButton = styled.button`
    position: absolute;
    top: 4px;
    right: 4px;
    width: 22px;
    height: 22px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: none;
    border-radius: 50%;
    background: rgba(15, 23, 42, 0.72);
    color: #ffffff;
    cursor: pointer;
    padding: 0;
    line-height: 1;

    &:hover {
        background: rgba(15, 23, 42, 0.9);
    }

    svg {
        width: 12px;
        height: 12px;
    }
`;
